import React from "react";
import { useSelector } from "react-redux";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import moment from "moment";
import cx from "classnames";
import _ from "lodash";

import InfoTooltip from "src/components/tooltip";
import { LoadingState, EmptyState } from "./states";
import styles from "./common.module.scss";

interface LeadsAreaChartDatum {
  key: string;
  value: number;
}

interface LeadsAreaChartProps {
  data?: LeadsAreaChartDatum[];
  title?: string;
  description?: string;
  legend?: { key: string; value: string };
  height?: number;
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  valueFormatter?: (value: number) => string;
  // eslint-disable-next-line no-unused-vars
  yAxisFormatter?: (value: number) => string;
}

const safeDateFormat = (value: any, mask: string): string => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = moment(value, "DD-MM-YYYY", true);
  if (!parsed.isValid()) return String(value);
  return parsed.format(mask);
};

let gradientSeq = 0;

export const LeadsAreaChart: React.FC<LeadsAreaChartProps> = ({
  data = [],
  title,
  description,
  legend = { key: "Date", value: "Leads" },
  height = 380,
  loading,
  valueFormatter,
  yAxisFormatter,
}) => {
  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor") || "#e51058",
  );
  const gradientId = React.useMemo(() => `leadsAreaGradient-${gradientSeq++}`, []);

  if (loading) return <LoadingState style={{ minHeight: height }} />;
  if (!data || data.length === 0) return <EmptyState style={{ minHeight: height }} />;

  return (
    <div className={styles.barChart}>
      {title && (
        <div className={styles.barChartHeader}>
          <span className={styles.barChartTitle}>{title}</span>
          {description && (
            <InfoTooltip placement="top" content={description}>
              <i className={cx(styles.barChartInfo, "paperflite-info")} />
            </InfoTooltip>
          )}
        </div>
      )}

      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 40, right: 0, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor || "#e51058"} stopOpacity={0.35} />
              <stop offset="95%" stopColor={primaryColor || "#e51058"} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="key"
            axisLine={false}
            tickCount={100}
            tickFormatter={(tickItem) => safeDateFormat(tickItem, "DD-MMM")}
          />
          <YAxis
            dataKey="value"
            axisLine={false}
            orientation="left"
            tickLine={false}
            domain={[0, "auto"]}
            tickFormatter={yAxisFormatter}
          />
          <Tooltip
            cursor={false}
            content={(props) => (
              <LeadsAreaTooltip {...props} legend={legend} valueFormatter={valueFormatter} />
            )}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={primaryColor || "#e51058"}
            strokeWidth={1}
            fill={`url(#${gradientId})`}
            dot={data.length === 1}
            activeDot
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const LeadsAreaTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  legend: { key: string; value: string };
  // eslint-disable-next-line no-unused-vars
  valueFormatter?: (value: number) => string;
}> = ({ active, payload, legend, valueFormatter }) => {
  if (!active || !payload || !payload.length) return null;

  const datum = _.get(payload, "[0].payload", null);
  if (!datum) return null;

  const rawValue = _.get(datum, "value", "");
  const value = valueFormatter ? valueFormatter(rawValue) : rawValue;

  return (
    <div className={styles.barChartTooltip}>
      <p className="key">{`${legend.key} : ${safeDateFormat(_.get(datum, "key", ""), "DD-MMM-YYYY")}`}</p>
      <p className="value">{`${legend.value} : ${value}`}</p>
    </div>
  );
};
